<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { getFileInfo } from '@chatwoot/utils';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { formatBytes } from 'shared/helpers/FileHelper';
import { downloadAttachment } from 'shared/helpers/downloadAttachment';
import { messageTimestamp } from 'shared/helpers/timeHelper';

import FileIcon from 'next/icon/FileIcon.vue';
import Icon from 'next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const MEDIA_FILE_TYPES = ['image', 'video', 'ig_reel', 'story_mention'];
const AUDIO_FILE_TYPES = ['audio'];
const DOCUMENT_FILE_TYPES = ['file'];
const GALLERY_FILE_TYPES = [
  'image',
  'video',
  'ig_reel',
  'audio',
  'story_mention',
];

const TABS = {
  MEDIA: 'media',
  DOCUMENTS: 'documents',
  AUDIO: 'audio',
};

const { t } = useI18n();
const store = useStore();
const selectedChatAttachments = useMapGetter('getSelectedChatAttachments');

const isLoading = ref(false);
const activeTab = ref(TABS.MEDIA);
const showGallery = ref(false);
const galleryAttachment = ref(null);
const downloadingKey = ref('');
const failedPreviewKeys = ref({});

const normalizedAttachments = computed(() => {
  return (selectedChatAttachments.value || [])
    .map(attachment => useSnakeCase(attachment))
    .filter(attachment => attachment.data_url || attachment.thumb_url)
    .sort((a, b) => (b.created_at || 0) - (a.created_at || 0));
});

const mediaAttachments = computed(() =>
  normalizedAttachments.value.filter(attachment =>
    MEDIA_FILE_TYPES.includes(attachment.file_type)
  )
);

const documentAttachments = computed(() =>
  normalizedAttachments.value.filter(attachment =>
    DOCUMENT_FILE_TYPES.includes(attachment.file_type)
  )
);

const audioAttachments = computed(() =>
  normalizedAttachments.value.filter(attachment =>
    AUDIO_FILE_TYPES.includes(attachment.file_type)
  )
);

const galleryAttachments = computed(() =>
  normalizedAttachments.value
    .filter(attachment => GALLERY_FILE_TYPES.includes(attachment.file_type))
    .map(attachment =>
      attachment.file_type === 'story_mention'
        ? { ...attachment, file_type: 'image' }
        : attachment
    )
);

const tabs = computed(() => [
  {
    key: TABS.MEDIA,
    label: t('CONVERSATION_SIDEBAR.MEDIA.TAB_LABEL', {
      label: t('CONVERSATION_SIDEBAR.MEDIA.TABS.MEDIA'),
      count: mediaAttachments.value.length,
    }),
  },
  {
    key: TABS.DOCUMENTS,
    label: t('CONVERSATION_SIDEBAR.MEDIA.TAB_LABEL', {
      label: t('CONVERSATION_SIDEBAR.MEDIA.TABS.DOCUMENTS'),
      count: documentAttachments.value.length,
    }),
  },
  {
    key: TABS.AUDIO,
    label: t('CONVERSATION_SIDEBAR.MEDIA.TAB_LABEL', {
      label: t('CONVERSATION_SIDEBAR.MEDIA.TABS.AUDIO'),
      count: audioAttachments.value.length,
    }),
  },
]);

const emptyStateMessage = computed(() => {
  if (activeTab.value === TABS.DOCUMENTS) {
    return t('CONVERSATION_SIDEBAR.MEDIA.EMPTY_DOCUMENTS');
  }
  if (activeTab.value === TABS.AUDIO) {
    return t('CONVERSATION_SIDEBAR.MEDIA.EMPTY_AUDIO');
  }
  return t('CONVERSATION_SIDEBAR.MEDIA.EMPTY_MEDIA');
});

const currentList = computed(() => {
  if (activeTab.value === TABS.DOCUMENTS) return documentAttachments.value;
  if (activeTab.value === TABS.AUDIO) return audioAttachments.value;
  return mediaAttachments.value;
});

const attachmentKey = attachment =>
  String(attachment.id || `${attachment.message_id}-${attachment.data_url}`);

const isVideo = attachment =>
  ['video', 'ig_reel'].includes(attachment.file_type);

const previewSrc = attachment => {
  const key = attachmentKey(attachment);
  const failed = failedPreviewKeys.value[key];
  if (failed === 'all') return '';
  if (failed === 'data_url') return attachment.thumb_url || '';
  // Prefer the original file URL. ActiveStorage variant/thumb URLs often 404.
  return attachment.data_url || attachment.thumb_url || '';
};

const hasPreviewFailed = attachment =>
  failedPreviewKeys.value[attachmentKey(attachment)] === 'all';

const onPreviewError = attachment => {
  const key = attachmentKey(attachment);
  const failed = failedPreviewKeys.value[key];
  if (!failed && attachment.data_url && attachment.thumb_url) {
    failedPreviewKeys.value = { ...failedPreviewKeys.value, [key]: 'data_url' };
    return;
  }
  failedPreviewKeys.value = { ...failedPreviewKeys.value, [key]: 'all' };
};

const fileDetails = attachment => {
  return getFileInfo(attachment.data_url || '');
};

const fileDisplayName = attachment => {
  const details = fileDetails(attachment);
  if (details?.name) return details.name;
  return t('CONVERSATION_SIDEBAR.MEDIA.UNKNOWN_FILE');
};

const fileExtension = attachment => {
  if (attachment.extension) {
    return String(attachment.extension).replace(/^\./, '');
  }
  return fileDetails(attachment)?.type || '';
};

const formattedSize = attachment => {
  if (!attachment.file_size) return '';
  return formatBytes(attachment.file_size, 1);
};

const formattedDate = attachment => {
  if (!attachment.created_at) return '';
  return messageTimestamp(attachment.created_at, 'dd/MM/yyyy') || '';
};

const fileMeta = attachment => {
  return [formattedSize(attachment), formattedDate(attachment)]
    .filter(Boolean)
    .join(' · ');
};

const fetchAttachments = async () => {
  if (!props.conversationId) return;
  const alreadyLoaded = selectedChatAttachments.value.length > 0;
  if (!alreadyLoaded) isLoading.value = true;
  try {
    await store.dispatch('fetchAllAttachments', props.conversationId);
  } finally {
    isLoading.value = false;
  }
};

const openGallery = attachment => {
  const prepared =
    attachment.file_type === 'story_mention'
      ? { ...attachment, file_type: 'image' }
      : attachment;
  galleryAttachment.value = prepared;
  showGallery.value = true;
};

const closeGallery = () => {
  showGallery.value = false;
  galleryAttachment.value = null;
};

const onDownload = async attachment => {
  const key = attachmentKey(attachment);
  try {
    downloadingKey.value = key;
    await downloadAttachment({
      url: attachment.data_url,
      type: attachment.file_type,
      extension: attachment.extension,
    });
  } catch {
    useAlert(t('GALLERY_VIEW.ERROR_DOWNLOADING'));
  } finally {
    downloadingKey.value = '';
  }
};

watch(
  () => props.conversationId,
  (newId, prevId) => {
    if (newId && newId !== prevId) {
      activeTab.value = TABS.MEDIA;
      failedPreviewKeys.value = {};
      fetchAttachments();
    }
  }
);

onMounted(() => {
  fetchAttachments();
});
</script>

<template>
  <div class="px-3 py-3">
    <div class="flex gap-1 p-1 rounded-lg bg-n-alpha-1">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        type="button"
        class="flex-1 min-w-0 rounded-md px-1 py-1.5 text-xs font-medium truncate transition-colors"
        :class="
          activeTab === tab.key
            ? 'bg-n-solid-active text-n-blue-11 shadow-sm'
            : 'text-n-slate-11 hover:text-n-slate-12'
        "
        @click="activeTab = tab.key"
      >
        {{ tab.label }}
      </button>
    </div>

    <div v-if="isLoading" class="flex justify-center items-center py-6">
      <Spinner size="28" class="text-n-brand" />
    </div>

    <div
      v-else-if="!currentList.length"
      class="px-2 py-6 text-center text-sm text-n-slate-11"
    >
      {{ emptyStateMessage }}
    </div>

    <div
      v-else-if="activeTab === TABS.MEDIA"
      class="grid grid-cols-3 gap-1 mt-3 max-h-80 overflow-y-auto"
    >
      <button
        v-for="attachment in mediaAttachments"
        :key="attachmentKey(attachment)"
        type="button"
        class="relative aspect-square overflow-hidden rounded-md bg-n-slate-3"
        @click="openGallery(attachment)"
      >
        <div
          v-if="hasPreviewFailed(attachment)"
          class="flex items-center justify-center size-full text-n-slate-11"
        >
          <Icon
            :icon="isVideo(attachment) ? 'i-lucide-video' : 'i-lucide-image'"
            class="size-6"
          />
        </div>
        <video
          v-else-if="isVideo(attachment)"
          :src="previewSrc(attachment)"
          class="size-full object-cover object-center"
          muted
          playsinline
          preload="metadata"
          @error="onPreviewError(attachment)"
        />
        <img
          v-else
          :src="previewSrc(attachment)"
          :alt="fileDisplayName(attachment)"
          class="size-full object-cover object-center"
          @error="onPreviewError(attachment)"
        />
        <span
          v-if="isVideo(attachment) && !hasPreviewFailed(attachment)"
          class="absolute inset-0 flex items-center justify-center bg-black/20"
        >
          <span
            class="flex items-center justify-center size-7 rounded-full bg-black/50"
          >
            <Icon
              icon="i-teenyicons-play-small-solid"
              class="size-6 text-white"
            />
          </span>
        </span>
      </button>
    </div>

    <div v-else class="flex flex-col gap-1 mt-3 max-h-80 overflow-y-auto">
      <div
        v-for="attachment in currentList"
        :key="attachmentKey(attachment)"
        class="flex items-center gap-2 rounded-md px-1.5 py-1.5 hover:bg-n-alpha-2"
      >
        <button
          type="button"
          class="flex items-center gap-2 min-w-0 flex-1 text-left"
          @click="
            activeTab === TABS.AUDIO
              ? openGallery(attachment)
              : onDownload(attachment)
          "
        >
          <span
            class="flex items-center justify-center size-8 rounded-md bg-n-alpha-2 shrink-0 text-n-slate-11"
          >
            <Icon
              v-if="activeTab === TABS.AUDIO"
              icon="i-lucide-mic"
              class="size-4"
            />
            <FileIcon v-else :file-type="fileExtension(attachment)" />
          </span>
          <span class="min-w-0 flex-1">
            <span
              class="block truncate text-sm text-n-slate-12"
              :title="fileDisplayName(attachment)"
            >
              {{
                activeTab === TABS.AUDIO
                  ? t('CONVERSATION_SIDEBAR.MEDIA.AUDIO_ITEM')
                  : fileDisplayName(attachment)
              }}
            </span>
            <span class="block truncate text-xs text-n-slate-11">
              {{ fileMeta(attachment) }}
            </span>
          </span>
        </button>
        <button
          v-tooltip="t('CONVERSATION_SIDEBAR.MEDIA.DOWNLOAD')"
          type="button"
          class="flex items-center justify-center size-8 shrink-0 text-n-slate-11 hover:text-n-slate-12"
          :disabled="downloadingKey === attachmentKey(attachment)"
          @click="onDownload(attachment)"
        >
          <Spinner
            v-if="downloadingKey === attachmentKey(attachment)"
            :size="14"
          />
          <Icon v-else icon="i-lucide-download" class="size-4" />
        </button>
      </div>
    </div>

    <GalleryView
      v-if="showGallery && galleryAttachment"
      v-model:show="showGallery"
      :attachment="galleryAttachment"
      :all-attachments="galleryAttachments"
      @close="closeGallery"
    />
  </div>
</template>
